import {
  AccountingTypeModel,
  CostsModel,
  ExpenditureBudgetPurposeModel,
} from '@/features/master-table/models';
import { t } from '@/i18n';
import { DayjsSchema, booleanStringType, sortOrderType } from '@/utils';
import {
  fundSheetTypeEnum,
  implementationMethodEnum,
  projectCategoryEnum,
  reviewTypeEnum,
  sheetTypeEnum,
} from '@/utils/enum';
import { z } from 'zod';
import { FundAdditionalIncomeAndExpenditureModel } from './fund-additiona-income-and-expenditure';
import { IndirectCostModel } from './payment';
import { SegmentExpenditureAndExecutionModel } from './segment-expenditure-and-execution';
import {
  FundBusinessForLocalGovernmentModel,
  SubProjectsModel,
} from './sub-project';

/// //////////////////////////////////////////////////
/// Enum
/// /////////////////////////////////////////////////

export const projectRelationType = z.enum([
  'split-target',
  'split-base',
  'merge-target',
  'merge-base',
  'ks-link-target',
  'ks-link-base',
  'ss-link-target',
  'ss-link-base',
  'parent-child-target',
  'parent-child-base',
  'others-target',
  'others-base',
]);
export const ProjectRelationTypeLabelModifier = {
  'split-target': t('project-relation-split-target'),
  'split-base': t('project-relation-split-base'),
  'merge-target': t('project-relation-merge-target'),
  'merge-base': t('project-relation-merge-base'),
  'ks-link-target': t('project-relation-ks-link-target'),
  'ks-link-base': t('project-relation-ks-link-base'),
  'ss-link-target': t('project-relation-ss-link-target'),
  'ss-link-base': t('project-relation-ss-link-base'),
  'parent-child-target': t('project-relation-parent-child-target'),
  'parent-child-base': t('project-relation-parent-child-base'),
  'others-target': t('project-relation-others-target'),
  'others-base': t('project-relation-others-base'),
} as const satisfies Record<z.infer<typeof projectRelationType>, string>;

export const corporateForm = z.enum([
  'general-incorporated-foundation',
  'general-incorporated-association',
  'public-interest-incorporated-foundation',
  'public-interest-incorporated-association',
  'national-research-and-development-agency',
  'special-corporation',
  'specified-nonprofit-corporation',
  'corporation-established-by-special-law',
  'private-corporation-established-by-special-law',
  'independent-administrative-agency',
  'authorized-corporation',
  'others',
]);

export const CorporateFormLabelModifier = {
  'general-incorporated-foundation': t('general-incorporated-foundation'),
  'general-incorporated-association': t('general-incorporated-association'),
  'public-interest-incorporated-foundation': t(
    'public-interest-incorporated-foundation',
  ),
  'public-interest-incorporated-association': t(
    'public-interest-incorporated-association',
  ),
  'national-research-and-development-agency': t(
    'national-research-and-development-agency',
  ),
  'special-corporation': t('special-corporation'),
  'specified-nonprofit-corporation': t('specified-nonprofit-corporation'),
  'corporation-established-by-special-law': t(
    'corporation-established-by-special-law',
  ),
  'private-corporation-established-by-special-law': t(
    'private-corporation-established-by-special-law',
  ),
  'independent-administrative-agency': t('independent-administrative-agency'),
  'authorized-corporation': t('authorized-corporation'),
  others: t('others-free-text'),
} as const satisfies Record<z.infer<typeof corporateForm>, string>;

export const operationForm = z.enum([
  'reduce',
  'turnover',
  'possession',
  'operation',
  'others',
]);
export const OperationFormLabelModifier = {
  reduce: t('reduce'),
  turnover: t('turnover'),
  possession: t('possession'),
  operation: t('operation'),
  others: t('others'),
} as const satisfies Record<z.infer<typeof operationForm>, string>;

export const businessForm = z.enum([
  'loan',
  'debt-guarantee',
  'interest-subsidy',
  'subsidy',
  'compensation',
  'investment',
  'investigation',
  'others',
]);
export const BusinessFormLabelModifier = {
  loan: t('loan'),
  'debt-guarantee': t('debt-guarantee'),
  'interest-subsidy': t('interest-subsidy'),
  subsidy: t('subsidy'),
  compensation: t('compensation'),
  investment: t('investment'),
  investigation: t('investigation'),
  others: t('others'),
} as const satisfies Record<z.infer<typeof businessForm>, string>;

export const opinionByAdministrativeReviewTeam = z.enum([
  'abolition',
  'drastic-improvement',
  'partial-improvement',
  'scheduled-end',
  'status-quo',
]);
export const OpinionByAdministrativeReviewTeamLabelModifier = {
  abolition: t('inspection-evaluation-opinion-abolition'),
  'drastic-improvement': t('inspection-evaluation-opinion-drastic-improvement'),
  'partial-improvement': t('inspection-evaluation-opinion-partial-improvement'),
  'scheduled-end': t('inspection-evaluation-opinion-scheduled-end'),
  'status-quo': t('inspection-evaluation-opinion-status-quo'),
} as const satisfies Record<
  z.infer<typeof opinionByAdministrativeReviewTeam>,
  string
>;

export const actionOfReflectionStatus = z.enum([
  'abolition',
  'reduction',
  'improvement',
  'consideration',
  'scheduled-end',
  'status-quo',
]);
export const ActionOfReflectionStatusLabelModifier = {
  abolition: t('inspection-evaluation-action-abolition'),
  reduction: t('inspection-evaluation-action-reduction'),
  improvement: t('inspection-evaluation-action-improvement'),
  consideration: t('inspection-evaluation-action-consideration'),
  'scheduled-end': t('inspection-evaluation-action-scheduled-end'),
  'status-quo': t('inspection-evaluation-action-status-quo'),
} as const satisfies Record<z.infer<typeof actionOfReflectionStatus>, string>;

export const fundNecessityType = z.enum([
  'occurrence-of-uncertain-accidents',
  'anticipation-of-collection-of-funds',
  'dependence-on-other-project',
  'others',
]);
export const FundNecessityTypeLabelModifier = {
  'occurrence-of-uncertain-accidents': t('occurrence-of-uncertain-accidents'),
  'anticipation-of-collection-of-funds': t(
    'anticipation-of-collection-of-funds',
  ),
  'dependence-on-other-project': t('dependence-on-other-project'),
  others: t('others'),
} as const satisfies Record<z.infer<typeof fundNecessityType>, string>;

export const meansOfDelivery = z.enum(['direct', 'indirect']);
export const MeansOfDeliveryLabelModifier = {
  direct: t('direct-delivery'),
  indirect: t('indirect-delivery'),
} as const satisfies Record<z.infer<typeof meansOfDelivery>, string>;

export const fundBudgetType = z.enum([
  'initial',
  'supplementary1',
  'supplementary2',
  'supplementary3',
  'supplementary4',
  'supplementary5',
  'reserve',
  'others',
]);
export const FundBudgetTypeLabelModifier = {
  initial: t('initial'),
  supplementary1: t('supplementary1'),
  supplementary2: t('supplementary2'),
  supplementary3: t('supplementary3'),
  supplementary4: t('supplementary4'),
  supplementary5: t('supplementary5'),
  reserve: t('reserve-fund'),
  others: t('others'),
} as const satisfies Record<z.infer<typeof fundBudgetType>, string>;

export const ExistenceOrAbsenceLabelModifier = {
  true: t('existence'),
  false: t('absence'),
} as const satisfies Record<z.infer<typeof booleanStringType>, string>;

export const reasonForNotSettingEndPeriod = z.enum([
  'no-rule-for-end',
  'fear-of-impact-on-international-negotiations',
  'to-continue-to-provide-relief-to-victims-of-clime',
  'others',
]);
export const ReasonForNotSettingEndPeriodLabelModifier = {
  'no-rule-for-end': t('no-rule-for-end'),
  'fear-of-impact-on-international-negotiations': t(
    'fear-of-impact-on-international-negotiations',
  ),
  'to-continue-to-provide-relief-to-victims-of-clime': t(
    'to-continue-to-provide-relief-to-victims-of-clime',
  ),
  others: t('other-rational-reasons'),
} as const satisfies Record<
  z.infer<typeof reasonForNotSettingEndPeriod>,
  string
>;

export const skipAdministrativeReformTeamEvaluationReason = z.enum([
  'non-related-expense-1-1',
  'non-related-expense-2-2',
  'non-related-expense-3-1',
  'non-related-expense-3-2',
  'non-related-expense-3-7',
  'non-related-expense-3-11',
  'non-related-expense-3-19',
  'non-related-expense-3-22',
  'non-related-expense-3-25',
  'non-related-expense-3-other',
  'similar-expense-1',
  'similar-expense-2',
  'similar-expense-3',
  'similar-expense-4',
  'similar-expense-5',
]);
export const SkipAdministrativeReformTeamEvaluationReasonLabelModifier = {
  'non-related-expense-1-1': t('non-related-expense-1-1'),
  'non-related-expense-2-2': t('non-related-expense-2-2'),
  'non-related-expense-3-1': t('non-related-expense-3-1'),
  'non-related-expense-3-2': t('non-related-expense-3-2'),
  'non-related-expense-3-7': t('non-related-expense-3-7'),
  'non-related-expense-3-11': t('non-related-expense-3-11'),
  'non-related-expense-3-19': t('non-related-expense-3-19'),
  'non-related-expense-3-22': t('non-related-expense-3-22'),
  'non-related-expense-3-25': t('non-related-expense-3-25'),
  'non-related-expense-3-other': t('non-related-expense-3-other'),
  'similar-expense-1': t('similar-expense-1'),
  'similar-expense-2': t('similar-expense-2'),
  'similar-expense-3': t('similar-expense-3'),
  'similar-expense-4': t('similar-expense-4'),
  'similar-expense-5': t('similar-expense-5'),
} as const satisfies Record<
  z.infer<typeof skipAdministrativeReformTeamEvaluationReason>,
  string
>;

export const inspectionByExternalExpertReason = z.enum([
  'start-from-previous-year',
  'final-fiscal-year',
  'targeted-at-the-meeting',
  'with-qualitative-indicator',
  'not-run-for-5-years',
  'others',
]);
export const InspectionByExternalExpertReasonModifier = {
  'start-from-previous-year': t('start-from-previous-year'),
  'final-fiscal-year': t('final-fiscal-year'),
  'targeted-at-the-meeting': t('targeted-at-the-meeting'),
  'with-qualitative-indicator': t('with-qualitative-indicator'),
  'not-run-for-5-years': t('not-run-for-5-years'),
  others: t('others'),
} as const satisfies Record<
  z.infer<typeof inspectionByExternalExpertReason>,
  string
>;

export const candidateReasonOfInspectionByExternalExpert = z.enum([
  'start-last-year',
  /**今年が最終年度の事業（今年が、事業の最終年度） */
  'final-year',
  /**現年度が長期アウトカムの目標年度(チェックON)or最終目標年度であるもの */
  'outcome-target-year',
  /**定性的アウトカムが設定されているもの */
  'has-qualitative-outcome',
  /**点検対象の最終実施年度が4年前以前のもの(2025現年度だと最終実施年度が2020年度以前に設定されているもの)  */
  'not-inspected-for-four-years',
  /**最終実施年度が設定されていないもの */
  'null-last-implemented-fiscal-year',
]);

export const CandidateReasonOfInspectionByExternalExpertModifier = {
  'start-last-year': t(
    'candidate-reason-of-inspection-by-external-expert-start-last-year',
  ),
  'final-year': t(
    'candidate-reason-of-inspection-by-external-expert-final-year',
  ),
  'outcome-target-year': t(
    'candidate-reason-of-inspection-by-external-expert-outcome-target-year',
  ),
  'has-qualitative-outcome': t(
    'candidate-reason-of-inspection-by-external-expert-qualitative-outcome',
  ),
  'not-inspected-for-four-years': t(
    'candidate-reason-of-inspection-by-external-expert-not-inspected-for-four-years',
  ),
  'null-last-implemented-fiscal-year': t(
    'candidate-reason-of-inspection-by-external-expert-null-last-implemented-fiscal-year',
  ),
} as const satisfies Record<
  z.infer<typeof candidateReasonOfInspectionByExternalExpert>,
  string
>;

export const processType = z.enum(['public-process', 'autumn-review']);
export const ProcessTypeLabelModifier = {
  'public-process': t('public-process'),
  'autumn-review': t('autumn-review'),
} as const satisfies Record<z.infer<typeof processType>, string>;

export const operationType = z.enum(['auto', 'manual']);
export const OperationTypeLabelModifier = {
  auto: t('auto'),
  manual: t('manual'),
} as const satisfies Record<z.infer<typeof operationType>, string>;

/// //////////////////////////////////////////////////
/// Model
/// /////////////////////////////////////////////////

export const BudgetAmountModel = z.object({
  id: z.string().uuid(),
  fiscalYear: z.number().int().nullable(),
  initialBudgetsTotalAmount: z.number().nullish(),
});

const SummaryAndResponseModel = z.object({
  process: processType.nullish(),
  fiscalYear: z.number().int().nullish(),
  content: z.string().nullish(),
  response: z.string().nullish(),
});

const OtherIndicatedItemAndResponseModel = z.object({
  reason: z.string().nullish(),
  fiscalYear: z.number().int().nullish(),
  content: z.string().nullish(),
  response: z.string().nullish(),
});

const FundOwnerShipRatio = z.object({
  operationType: operationType.nullish(),
  manualRatio: z.number().nullish(),
  ratioNumerator: z.number().nullish(),
  ratioDenominator: z.number().nullish(),
  basis: z
    .object({
      calculationFormula: z.string().nullish(), // 2025年度以降の事業では使わない
      contents: z.string().nullish(), // 2025年度以降の事業では使わない
      contentsOfHeldFundAmount: z.string().nullish(), // 2025年度より前の事業では使わない
      contentsOfFundProjectCosts: z.string().nullish(), // 2025年度より前の事業では使わない
    })
    .nullish(),
  conceptOfProjectProspect: z
    .object({
      calculationFormula: z.string().nullish(), // 2025年度以降の事業では使わない
      contents: z.string().nullish(),
      basisForCalculationOfIndicators: z.string().nullish(),
      moreRecentResults: z.string().nullish(),
    })
    .nullish(),
});

const FundLowUsedFundInvestigationModel = z.object({
  hasCeasedOperations: z.boolean().nullish(),
  hasNoResultRecent: z.boolean().nullish(),
  hasFundThatLostPurpose: z.boolean().nullish(),
  hasExceedOneSignificantly: z.boolean().nullish(),
  hasDeemedUnlikelyToBeUsed: z.boolean().nullish(),
  reasonsForBeingFalseWhileExceedingOne: z.string().nullish(),
  resultsOfInvestigations: z.string().nullish(),
  reasonsForLeaving: z.string().nullish(),
});

const AdditionalJurisdictionalUsersModel = z.object({
  ministryId: z.string().uuid(),
  organizationId: z.string().uuid(),
  businessManagerIds: z.string().uuid().array().min(1),
  peopleInChargeIds: z.string().uuid().array().min(1),
});

export const InspectionEvaluationRsModel = z.object({
  inspectionAndImprovementByAdministrativeJurisdiction: z
    .object({
      inspectionResult: z.string().nullish(),
      evaluation: z.string().nullish(),
      directionOfImprovement: z.string().nullish(),
    })
    .nullish(),
  inspectionByExternalExpert: z
    .object({
      reviewType: reviewTypeEnum.nullish(),
      lastImplementedFiscalYear: z.number().int().nullish(),
      reason: inspectionByExternalExpertReason.nullish(),
      opinion: z.string().nullish(),
      resultOfPublishing: z.string().nullish(),
    })
    .nullish(),
  processAndOpinionByAdministrativeReviewTeam: z
    .object({
      opinion: opinionByAdministrativeReviewTeam.nullish(),
      context: z.string().nullish(),
    })
    .nullish(),
  reflectionStatusBasedOnOpinion: z
    .object({
      action: actionOfReflectionStatus.nullish(),
      reflectedAmountOfGeneralAccount: z.number().nullish(),
      specialAccount: z
        .object({
          accountingTypeId: z.string().uuid().nullish(),
          accountTitleId: z.string().uuid().nullish(),
          reflectedAmount: z.number().nullish(),
        })
        .array()
        .nullish(),
      context: z.string().nullish(),
    })
    .nullish(),
  summariesAndResponses: SummaryAndResponseModel.array(),
  otherIndicatedItemsAndResponses: OtherIndicatedItemAndResponseModel.array(),
});

export const InspectionEvaluationSsModel = z.object({
  inspectionAndImprovementByAdministrativeJurisdiction: z.object({
    inspectionResult: z.string().nullish(),
    evaluation: z.string().nullish(),
    directionOfImprovement: z.string().nullish(),
  }),
});

export const InspectionEvaluationKsModel = z.object({
  inspectionAndImprovementByAdministrativeJurisdiction: z.object({
    inspectionResult: z.string().nullish(),
    evaluation: z.string().nullish(),
    directionOfImprovement: z.string().nullish(),
  }),
  findingsOfExternalExperts: z.string().nullish(),
  processLeadingToTheFindingsOfReviewPromotionTeam: z.string().nullish(),
  improvementPointBasedOnTheFindings: z.string().nullish(),
  overviewOfReviewsCarriedOutInThePast: z.string().nullish(),
  ownershipRatio: FundOwnerShipRatio.nullish(),
  lowUsedFundInvestigation: FundLowUsedFundInvestigationModel.nullish(),
  appropriatenessInspection: z
    .object({
      reasonsWhyBulkDeliveringWasNecessary: z.string().nullish(), // 2025年度以降の事業では使わない
      approachInDeterminingTheTimingAndAmountOfAdditions: z.string().nullish(), // 2025年度以降の事業では使わない
    })
    .nullish(),
  implementationStatusOfInspection: z.string().nullish(),
  eligibilityInspection: z
    .object({
      methodAndReasonsForSelection: z.string().nullish(),
      results: z.string().nullish(),
    })
    .nullish(),
});

const InspectionEvaluationModel = z.union([
  InspectionEvaluationRsModel,
  InspectionEvaluationKsModel,
  InspectionEvaluationSsModel,
  z.object({}),
]);

export const DiscriminatedInspectionEvaluationModel = z.discriminatedUnion(
  'sheetType',
  [
    z.object({
      sheetType: z.literal('RS'),
      inspectionEvaluation: InspectionEvaluationRsModel.partial(),
      skipAdministrativeReformTeamEvaluation: z.boolean(),
    }),
    z.object({
      sheetType: z.literal('SS'),
      inspectionEvaluation: InspectionEvaluationSsModel.partial(),
    }),
    z.object({
      sheetType: z.literal('KS'),
      inspectionEvaluation: InspectionEvaluationKsModel.partial(),
    }),
  ],
);

export const FundCompositionBackgroundsModel = z.object({
  fiscalYearOfBudgetaryProvision: z.number().int().nullish(),
  fiscalYearOfCreation: z.number().int().nullish(),
  meansOfDelivery: meansOfDelivery.nullish(),
  budget: fundBudgetType.nullish(),
  accountingId: z.string().nullish(),
  expenditurePurposeName: z.string().nullish(),
  amountOfStateExpenditure: z.number().nullish(),
  expenditureBudgetPurposes: ExpenditureBudgetPurposeModel.array().nullish(),
  haveAppliedLawOfAppropriatenessOfSubsidies: z.boolean().nullish(),
  relatedReviewSheetYear: z.number().int().nullish(),
  relatedReviewSheetId: z.string().uuid().nullish(),
  relatedReviewSheetName: z.string().nullish(),
});

const FundNationalTreasuryReturnBackgroundModel = z.object({
  fiscalYear: z.number().int().nullish(),
  amount: z.number().nullish(),
  reasons: z.string().nullish(),
});

const FundScheduledEndModel = z.object({
  endDate: z.string().nullish(),
  reasonsForNotSettingScheduledEndPeriod:
    reasonForNotSettingEndPeriod.nullish(), // 2025年度以降の事業では使わない
  supplementaryReasonsForEndPeriod: z.string().nullish(),
  reasonsForChangingScheduledEndPeriod: z.string().nullish(),
  endDateOfNewApplicationReceiving: z.string().nullish(),
  reasonsForNotSettingEndPeriodOfNewApplicationReceiving:
    reasonForNotSettingEndPeriod.nullish(), // 2025年度以降の事業では使わない
  supplementaryReasonsForNewApplicationReceiving: z.string().nullish(),
  reasonsForChangingEndPeriodOfNewApplicationReceiving: z.string().nullish(),
});

export const FundInvestmentsModel = z.object({
  accumulatedInvestmentsFromTheGovernment: z
    .object({
      accountingTypeId: z.string().uuid().nullish(),
      accountTitleId: z.string().uuid().nullish(),
      amount: z.number().int(),
      optionalAccountingTypeName: z.string().nullish(),
      optionalAccountTitleName: z.string().nullish(),
    })
    .array(),
  annualInvestmentsFromTheGovernment: z.number().nullish().array().nullish(),
  accumulatedInvestmentFromPrivateSectors: z.number().nullish(),
});

export const FundBudgetClassification = z.enum([
  'budget_required_consideration_for_next_action',
  'preparation_budget_for_unexpected',
  'no_future_budget',
]);
export const FundBudgetClassificationModifier = {
  [FundBudgetClassification.Values
    .budget_required_consideration_for_next_action]: t(
    'fund-budget-classification-budget-required-consideration-for-next-action',
  ),
  [FundBudgetClassification.Values.preparation_budget_for_unexpected]: t(
    'fund-budget-classification-preparation-budget-for-unexpected',
  ),
  [FundBudgetClassification.Values.no_future_budget]: t(
    'fund-budget-classification-no-future-budget',
  ),
};
/**
 * 2026年度以降の基金シートで使用する「基金の分類」の選択肢名。
 * budget_required_consideration_for_next_action のみ文言が変更されている。
 * 年度による出し分けは isBefore2026 で判定すること。
 */
export const FundBudgetClassificationModifierFrom2026 = {
  ...FundBudgetClassificationModifier,
  [FundBudgetClassification.Values
    .budget_required_consideration_for_next_action]: t(
    'fund-budget-classification-budget-required-consideration-for-certain-period',
  ),
};

export const FundAdditionalAttributesModel = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  fundSheetNumber: z.number().min(1).max(9999),
  fundSheetBranchNumber: z.number().min(1).max(99).nullish(),
  fundSheetType: fundSheetTypeEnum,
  fundBusinessName: z.string().nullish(),
  fundOwner: z.string().nullish(),
  corporateForm: corporateForm.nullish(),
  optionalCorporateForm: z.string().nullish(),
  operationForms: z.array(operationForm).nullish(),
  businessForms: z.array(businessForm).nullish(),
  effortAndResult: z.string().nullish(),
  effortAndResultAndOverview: z.string().nullish(),
  necessityType: fundNecessityType.nullish(),
  necessityReasons: z.string().nullish(),
  compositionBackgrounds: FundCompositionBackgroundsModel.array().nullish(),
  nationalTreasuryReturnBackgrounds:
    FundNationalTreasuryReturnBackgroundModel.array().nullish(),
  scheduledEnd: FundScheduledEndModel.nullish(),
  matterOfActForNormalizationOfGrant: z.string().nullish(),
  accountingTypes: AccountingTypeModel.array(),
  accountingTypeIds: AccountingTypeModel.shape.id.array(),
  numberOfLocalFundBuilding: z.number().int().nonnegative().nullable(),
  investments: FundInvestmentsModel.nullish(),
  personnelExpenses: z
    .object({
      choreExpense: z.number().nullish(),
      personnelExpense: z.number().nullish(),
    })
    .nullish(),
  executionDivergences: z
    .object({
      reason: z.string().nullish(),
      estimatedBusinessExpense: z.number().nullish(),
      actualBusinessExpense: z.number().nullish(),
    })
    .array()
    .nullish(),
  additionalJurisdictionalUsers:
    AdditionalJurisdictionalUsersModel.array().nullish(),
  fundBudgetClassification: FundBudgetClassification.nullish(), // 2025 年度以降のみ使用
  recentBudgetMeasureYear: z.string().nullish(), // 2025 年度以降のみ使用
  budgetReasonWithAchievementStatusAndReviewResult: z.string().nullish(), // 2025 年度以降のみ使用
  nextBudgetMeasureReviewYear: z.string().nullish(), // 2025 年度以降のみ使用
});

export const CabinetDecisionWithDefinedIndicatorsModel = z.object({
  name: z.string().nullish(),
  url: z.string().url().nullish(),
  relevantPart: z.string().nullish(),
});

export const RelatedProjectModel = z.object({
  id: z.string().uuid(),
  name: z.string(),
  fiscalYear: z.number().int(),
  relationType: projectRelationType,
});

export const LegalBasisModel = z.object({
  id: z.string().nullish(),
  number: z.string().nullish(),
  name: z.string().min(1),
  article: z.string().nullish(),
  paragraph: z.string().nullish(),
  paragraphSubIndex: z.string().nullish(),
  isManualInput: z.boolean().default(false),
});

export const PlanModel = z.object({
  url: z.preprocess((data) => data || null, z.string().url().nullish()),
  contents: z.string().nullish(),
});

export const PoliciesForProjectModel = z.object({
  policyId: z.string().uuid(),
  policyUrl: z.string().url().nullish(),
  projectId: z.string().uuid(),
});

export const SubsidyRateModel = z.object({
  target: z.string().nullish(),
  ratesText: z.string().nullish(),
  limitText: z.string().nullish(),
  url: z.preprocess((data) => data || null, z.string().url().nullish()),
});

// TODO: useProjectでこのモデルでパーズしているため、どれかしらのシートに存在パラメータはここにnullishで定義するようになってる。型補完が適切ではないのでシートごとに分ける
export const ProjectBaseModel = z.object({
  id: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lineageId: z.string().uuid(),
  sheetType: sheetTypeEnum,
  projectNumber: z.string().nullable(),
  oldProjectNumber: z.string().max(30).nullish(),
  ministryId: z.string().uuid(),
  name: z.string().min(1),
  segmentUnitConcept: z.string().nullish(),
  isPrivate: z.boolean(),
  privateReason: z.string().nullable(),
  privateReasonContext: z.string().nullable(),
  organization: z.object({
    id: z.string().uuid(),
    name: z.string(),
    code: z.string(),
    upperIds: z.string().uuid().array(),
    names: z.string().array(),
  }),
  /** @deprecated organizationを使う*/
  organizationId: z.string().uuid(),
  /** @deprecated independentAdministrativeAgencyを使う */
  independentAdministrativeAgencyId: z.string().uuid().nullable(),
  /** @deprecated relatedOrganizationsを使う */
  relatedOrganizationIds: z.string().uuid().array(),
  relatedOrganizations: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      code: z.string(),
      upperIds: z.string().uuid().array(),
      names: z.string().array(),
    })
    .array(),
  /** @deprecated peopleInChargeを使う */
  peopleInChargeIds: z.string().uuid().array().nullable(),
  /** @deprecated businessManagersを使う */
  businessManagerIds: z.string().uuid().array().min(1),
  peopleInCharge: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      organizations: z
        .object({
          id: z.string().uuid(),
          name: z.string(),
          code: z.string(),
        })
        .array(),
    })
    .array(),
  businessManagers: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      organizations: z
        .object({
          id: z.string().uuid(),
          name: z.string(),
          code: z.string(),
        })
        .array(),
    })
    .array(),
  fiscalYear: z.number().int(),
  startYear: z.number().int().nullable(),
  isUnknownStart: z.boolean().default(false),
  endYear: z.number().int().nullable(), // 2025年度以降の KS standard の事業では入力できないが、検索のために「基金事業の終了予定時期」の年度が入っている。
  isNoScheduledEnd: z.boolean().default(false),
  category: projectCategoryEnum.nullish(), // nullable only for KS
  legalBases: z.preprocess(
    // TODO: Replace the empty fallback array with proper handling for NULL values in the backend.
    // This is a temporary workaround.
    (data) => data ?? [],
    LegalBasisModel.array().nullish(),
  ),
  plans: PlanModel.array().nullish(),
  policiesForProject: PoliciesForProjectModel.array(),
  mainCosts: CostsModel,
  paymentIndirectCosts: IndirectCostModel.nullable(),
  purpose: z.string().nullish(),
  task: z.string().nullish(),
  overview: z.string().nullish(),
  overviewUrl: z.string().url().nullish(),
  // TODO: move to RS Model because it is required for RS but optional for others.
  implementationMethods: implementationMethodEnum.array().nullish(),
  subsidyRates: SubsidyRateModel.array().nullish(),
  inspectionEvaluation: InspectionEvaluationModel,
  relatedProjects: RelatedProjectModel.array(),
  remark: z.string().nullish(),
  increaseDecreaseMainReason: z.string().nullish(),
  otherSpecialNotes: z.string().nullish(),
  isDiscarded: z.boolean(),
  isLocked: z.boolean(),
  isPublished: z.boolean(),
  segmentExpenditureAndExecution: SegmentExpenditureAndExecutionModel.omit({
    fiscalYear: true,
  }).nullish(),
  skipAdministrativeReformTeamEvaluation: z.boolean().default(false),
  skipAdministrativeReformTeamEvaluationReason:
    skipAdministrativeReformTeamEvaluationReason.nullish(),
  skipAdministrativeReformTeamEvaluationReasonSupplement: z.string().nullish(),
  candidateReasonsOfInspectionByExternalExpert:
    candidateReasonOfInspectionByExternalExpert.array().nullish(),
  cabinetDecisionWithDefinedIndicators:
    CabinetDecisionWithDefinedIndicatorsModel.nullable(),
  // TODO move to KS model because it is required for KS but optional for others.
  fundAdditionalAttributes: FundAdditionalAttributesModel.nullable(),
  // TODO move to KS model because it is required for KS but optional for others.
  fundAdditionalIncomeAndExpenditure:
    FundAdditionalIncomeAndExpenditureModel.nullable(),
  // TODO move to KS model because it is required for KS but optional for others.
  fundBusinessForLocalGovernmentSummary:
    FundBusinessForLocalGovernmentModel.nullish(),
  sortOrder: sortOrderType.max(9999).nullish(),
  majorExpenditureBudgetPurposeId: z.string().uuid().nullish(),
  /** @deprecated otherResponsibleOrganizationsを使う */
  otherResponsibleOrganizationIds: z.string().uuid().array(),
  otherResponsibleOrganizations: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      code: z.string(),
      upperIds: z.string().uuid().array(),
      names: z.string().array(),
    })
    .array(),
  publishedAt: z.string().nullable(),
  lockedAt: z.string().nullable(),
  isConsecutive: z.boolean(),
  snapshotId: z.string().uuid().nullish(),
  additionalJurisdictionalBusinessManagers: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      organizations: z
        .object({
          id: z.string().uuid(),
          name: z.string(),
          code: z.string(),
        })
        .array(),
    })
    .array(),
  additionalJurisdictionalPeopleInCharge: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      organizations: z
        .object({
          id: z.string().uuid(),
          name: z.string(),
          code: z.string(),
        })
        .array(),
    })
    .array(),
  additionalJurisdictionalOrganizations: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      code: z.string(),
      upperIds: z.string().uuid().array(),
      names: z.string().array(),
    })
    .array(),
  independentAdministrativeAgency: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      code: z.string(),
      upperIds: z.string().uuid().array(),
      names: z.string().array(),
    })
    .nullish(),
});

export const ReviewSheetModel = ProjectBaseModel.omit({
  fundAdditionalAttributes: true,
  fundAdditionalIncomeAndExpenditure: true,
  segmentUnitConcept: true,
}).extend({
  sheetType: z.literal('RS'),
});

export const SegmentSheetModel = ProjectBaseModel.omit({
  policiesForProject: true,
  implementationMethods: true,
  fundAdditionalAttributes: true,
  fundAdditionalIncomeAndExpenditure: true,
}).extend({
  sheetType: z.literal('SS'),
  /** @deprecated */
  independentAdministrativeAgencyId:
    ProjectBaseModel.shape.independentAdministrativeAgencyId.unwrap(),
  independentAdministrativeAgency:
    ProjectBaseModel.shape.independentAdministrativeAgency.unwrap(),
});

export const FundSheetModel = ProjectBaseModel.omit({
  policiesForProject: true,
  implementationMethods: true,
  segmentUnitConcept: true,
}).extend({
  sheetType: z.literal('KS'),
  inspectionEvaluation: InspectionEvaluationKsModel.deepPartial(),
});

export const ProjectModel = ProjectBaseModel.extend({
  subProjects: SubProjectsModel,
});

export const CreateProjectResponseModel = ProjectBaseModel.pick({
  id: true,
});

export const UpdatedProjectSummaryModel = z.object({
  finalizedAmountForInitialBudget: z.number().nullable(),
  finalizedAmountForSupplementaryBudget: z.number().nullable(),
  requestedAmount: z.number().nullable(),
  previousYearExecutedAmount: z.number().nullable(),
  previousYearExecutedAmountForGeneralAccount: z.number().nullable(),
  previousYearExecutedAmountForSpecialAccount: z.number().nullable(),
  newlyStartedProjectCount: z.number().nullable(),
  previousYearExecutedAmountUnderNewlyStartedProject: z.number().nullable(),
  requestedAmountUnderNewlyStartedProject: z.number().nullable(),
  finalizedAmountForInitialBudgetUnderNewlyStartedProject: z
    .number()
    .nullable(),
  newlyRequestedProjectCount: z.number().nullable(),
  previousYearExecutedAmountUnderNewlyRequestedProject: z.number().nullable(),
  finalizedAmountForInitialBudgetUnderNewlyRequestedProject: z
    .number()
    .nullable(),
  requestedAmountUnderNewlyRequestedProject: z.number().nullable(),
  continuingProjectCount: z.number().nullable(),
  finalizedAmountForInitialBudgetUnderContinuingProject: z.number().nullable(),
  requestedAmountUnderContinuingProject: z.number().nullable(),
  previousYearExecutedAmountUnderContinuingProject: z.number().nullable(),
  finalizedAmountForGeneralAccountAndInitialBudget: z.number().nullable(),
  finalizedAmountForGeneralAccount: z.number().nullable(),
  requestedAmountForGeneralAccount: z.number().nullable(),
  finalizedAmountForSpecialAccountAndInitialBudget: z.number().nullable(),
  finalizedAmountForSpecialAccount: z.number().nullable(),
  requestedAmountForSpecialAccount: z.number().nullable(),
});

export const ProjectSummaryModel = z.object({
  result: z.string(),
  summary: UpdatedProjectSummaryModel,
});

export const GroupSummaryModel = z.object({
  summary: UpdatedProjectSummaryModel,
});

/// //////////////////////////////////////////////////
/// Schema
/// /////////////////////////////////////////////////

const InspectionEvaluationRsFormSchema = z.object({
  inspectionAndImprovementByAdministrativeJurisdiction:
    InspectionEvaluationRsModel.shape
      .inspectionAndImprovementByAdministrativeJurisdiction,
  inspectionByExternalExpert: z.object({
    reviewType: reviewTypeEnum.nullish(),
    reason: inspectionByExternalExpertReason.nullish(),
    opinion: z.string().nullish(),
    resultOfPublishing: z.string().nullish(),
    lastImplementedFiscalYear: DayjsSchema().nullish(),
  }),
  processAndOpinionByAdministrativeReviewTeam:
    InspectionEvaluationRsModel.shape
      .processAndOpinionByAdministrativeReviewTeam,
  reflectionStatusBasedOnOpinion:
    InspectionEvaluationRsModel.shape.reflectionStatusBasedOnOpinion,
  summariesAndResponses: SummaryAndResponseModel.extend({
    fiscalYear: DayjsSchema().nullish(),
  }).array(),
  otherIndicatedItemsAndResponses: OtherIndicatedItemAndResponseModel.extend({
    fiscalYear: DayjsSchema().nullish(),
  }).array(),
});

const InspectionEvaluationSsFormSchema = z.object({
  inspectionAndImprovementByAdministrativeJurisdiction:
    InspectionEvaluationSsModel.shape
      .inspectionAndImprovementByAdministrativeJurisdiction,
});

export const InspectionEvaluationKsFormSchema =
  InspectionEvaluationKsModel.pick({
    findingsOfExternalExperts: true,
    processLeadingToTheFindingsOfReviewPromotionTeam: true,
    improvementPointBasedOnTheFindings: true,
    overviewOfReviewsCarriedOutInThePast: true,
    ownershipRatio: true,
    lowUsedFundInvestigation: true,
    appropriatenessInspection: true,
    eligibilityInspection: true,
    implementationStatusOfInspection: true,
  }).extend({
    inspectionAndImprovementByAdministrativeJurisdiction:
      InspectionEvaluationKsModel.shape.inspectionAndImprovementByAdministrativeJurisdiction.deepPartial(),
    lowUsedFundInvestigation: FundLowUsedFundInvestigationModel.pick({
      reasonsForBeingFalseWhileExceedingOne: true,
      resultsOfInvestigations: true,
      reasonsForLeaving: true,
    }).extend({
      hasCeasedOperations: z.string().nullish(),
      hasNoResultRecent: z.string().nullish(),
      hasFundThatLostPurpose: z.string().nullish(),
      hasExceedOneSignificantly: z.string().nullish(),
      hasDeemedUnlikelyToBeUsed: z.string().nullish(),
    }),
  });

export const KsOwnershipRatioFormSchema =
  InspectionEvaluationKsFormSchema.extend({
    lowUsedFundInvestigation: FundLowUsedFundInvestigationModel.pick({
      reasonsForBeingFalseWhileExceedingOne: true,
      resultsOfInvestigations: true,
      reasonsForLeaving: true,
    }).extend({
      hasCeasedOperations: booleanStringType,
      hasNoResultRecent: booleanStringType,
      hasFundThatLostPurpose: booleanStringType,
      hasExceedOneSignificantly: booleanStringType,
      hasDeemedUnlikelyToBeUsed: booleanStringType,
    }),
  });

export const InspectionEvaluationFormSchema = z.union([
  InspectionEvaluationRsFormSchema,
  InspectionEvaluationKsFormSchema,
  InspectionEvaluationSsFormSchema,
]);

export const DiscriminatedInspectionEvaluationFormSchema = z.discriminatedUnion(
  'sheetType',
  [
    InspectionEvaluationRsFormSchema.extend({ sheetType: z.literal('RS') }),
    InspectionEvaluationSsFormSchema.extend({ sheetType: z.literal('SS') }),
    InspectionEvaluationKsFormSchema.extend({ sheetType: z.literal('KS') }),
  ],
);

export const KsAdditionalAttributesFormSchema =
  FundAdditionalAttributesModel.omit({
    id: true,
    projectId: true,
    executionDivergences: true,
    accountingTypes: true,
    accountingTypeIds: true,
    numberOfLocalFundBuilding: true,
  }).extend({
    compositionBackgrounds: FundCompositionBackgroundsModel.omit({
      expenditureBudgetPurposes: true,
    })
      .extend({
        fiscalYearOfBudgetaryProvision: DayjsSchema().nullish(),
        fiscalYearOfCreation: DayjsSchema().nullish(),
        haveAppliedLawOfAppropriatenessOfSubsidies: z.string().nullish(),
        relatedReviewSheetYear: DayjsSchema().nullish(),
        // uuidで縛ると過去に項までしか登録されていないデータがあった場合に更新ができない。PRZ-1397
        expenditureBudgetPurposeIds: z.string().array().array().optional(),
      })
      .deepPartial()
      .array(),
    nationalTreasuryReturnBackgrounds:
      FundNationalTreasuryReturnBackgroundModel.extend({
        fiscalYear: DayjsSchema().nullish(),
      })
        .deepPartial()
        .array(),
    scheduledEnd: FundScheduledEndModel.extend({
      endDate: DayjsSchema().nullish(),
      endDateOfNewApplicationReceiving: DayjsSchema().nullish(),
    }).partial(),
  });

export const KsAdditionalAttributesExecutionDivergencesFormSchema = z.object({
  executionDivergences: z
    .object({
      reason: z.string().nullish(),
      estimatedBusinessExpense: z.number().nullish(),
      actualBusinessExpense: z.number().nullish(),
    })
    .array()
    .nullish(),
});

export const ProjectOtherNotesFormSchema = ProjectBaseModel.pick({
  increaseDecreaseMainReason: true,
  otherSpecialNotes: true,
});

export const SsProjectOtherNotesFormSchema = ProjectBaseModel.pick({
  otherSpecialNotes: true,
});

export const FundInvestmentsFormSchema = FundInvestmentsModel.extend({
  accumulatedInvestmentsFromTheGovernment:
    FundInvestmentsModel.shape.accumulatedInvestmentsFromTheGovernment.optional(),
});

export const UpdateProjectResponseSchema = ProjectBaseModel.pick({
  majorExpenditureBudgetPurposeId: true,
}).extend({
  baseOrganizationChanged: z.boolean(),
});

export const ContinueProjectSchema = z.object({
  fiscalYear: DayjsSchema(),
});
